import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { useState, type FormEvent } from "react";
import type { CaseView, ProblemListItem } from "shared";
import { api } from "../api";
import { useCallerState } from "../callerState";
import { STATES } from "../states";

/** New case. Only the fields the portal needs: no Social Security, bank or card numbers. */
export function NewCase() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const caller = useCallerState();
  const problems = useQuery({ queryKey: ["problems"], queryFn: () => api<{ problems: ProblemListItem[] }>("/api/kb/problems") });
  const [f, setF] = useState({ name: "", phone: "", email: "", state: caller.state ?? "", zip: "", contact: "phone", title: "", problemId: "", urgency: "normal", summary: "" });
  const set = (k: keyof typeof f) => (e: { target: { value: string } }) => setF({ ...f, [k]: e.target.value });
  const create = useMutation({
    mutationFn: () => api<{ case: CaseView }>("/api/cases", {
      method: "POST",
      body: {
        customer: { name: f.name, phone: f.phone, email: f.email, stateCode: f.state, zip: f.zip, preferredContact: f.contact },
        title: f.title, problemId: f.problemId || null, urgency: f.urgency, summary: f.summary,
      },
    }),
    onSuccess: (r) => { qc.invalidateQueries({ queryKey: ["cases"] }); navigate({ to: "/cases/$id", params: { id: r.case.id } }); },
  });
  const submit = (e: FormEvent) => { e.preventDefault(); create.mutate(); };
  return (
    <form onSubmit={submit} className="card mx-auto max-w-2xl space-y-3 text-sm">
      <h1 className="text-xl font-semibold">New case</h1>
      <div className="grid gap-3 sm:grid-cols-2">
        <div><label className="label" htmlFor="nc-name">Customer name</label><input id="nc-name" className="input" required maxLength={120} value={f.name} onChange={set("name")} /></div>
        <div><label className="label" htmlFor="nc-phone">Phone</label><input id="nc-phone" className="input" type="tel" maxLength={40} value={f.phone} onChange={set("phone")} /></div>
        <div><label className="label" htmlFor="nc-email">Email</label><input id="nc-email" className="input" type="email" maxLength={200} value={f.email} onChange={set("email")} /></div>
        <div><label className="label" htmlFor="nc-state">State</label>
          <select id="nc-state" className="input" required value={f.state} onChange={set("state")}>
            <option value="">Choose…</option>{STATES.map((s) => <option key={s.code} value={s.code}>{s.name}</option>)}</select></div>
        <div><label className="label" htmlFor="nc-zip">ZIP</label><input id="nc-zip" className="input" inputMode="numeric" pattern="\d{5}" maxLength={5} value={f.zip} onChange={set("zip")} /></div>
        <div><label className="label" htmlFor="nc-contact">Preferred contact</label>
          <select id="nc-contact" className="input" value={f.contact} onChange={set("contact")}>
            <option value="phone">Phone</option><option value="email">Email</option><option value="sms">Text message</option></select></div>
      </div>
      <div><label className="label" htmlFor="nc-title">Title</label><input id="nc-title" className="input" required maxLength={200} value={f.title} onChange={set("title")} placeholder="e.g. Behind on rent" /></div>
      <div className="grid gap-3 sm:grid-cols-2">
        <div><label className="label" htmlFor="nc-problem">Problem (optional)</label>
          <select id="nc-problem" className="input" value={f.problemId} onChange={set("problemId")}>
            <option value="">Not sure yet</option>{problems.data?.problems.map((p) => <option key={p.id} value={p.id}>{p.title}</option>)}</select></div>
        <div><label className="label" htmlFor="nc-urgency">Urgency</label>
          <select id="nc-urgency" className="input" value={f.urgency} onChange={set("urgency")}>
            {["low", "normal", "high", "emergency"].map((u) => <option key={u} value={u}>{u}</option>)}</select></div>
      </div>
      <div><label className="label" htmlFor="nc-summary">Summary</label><textarea id="nc-summary" className="input min-h-24" maxLength={5000} value={f.summary} onChange={set("summary")} /></div>
      <p className="text-xs text-slate-500">Do not enter Social Security, bank or card numbers.</p>
      {create.isError && <p role="alert" className="text-red-600">Could not create the case. Check the fields and try again.</p>}
      <button type="submit" className="btn-primary" disabled={create.isPending}>Create case</button>
    </form>
  );
}
